import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DetectMethodChanges, findHCF, getShortUUID, isInstrumentExpired, isVoid} from "../utils";
import {ApgPortfolio} from "../adjustment-pricing-grid/model/ApgPortfolio";
import {HgPositionsSectionComponent} from "./positions-section/hg-positions-section.component";
import {HgSettingsSectionComponent} from "./settings-section/hg-settings-section.component";
import {CalculateCashFlowHedges, CalculateCashFlowHedgesReply} from "../shell-communication/shell-operations-protocol";
import {HedgePositionGroupsBySide} from "./data-model/hedge-position-groups-by-side";
import * as Enumerable from "linq";
import {HedgePositionGroup} from "./data-model/hedge-position-group";
import {ShellClientService} from "../shell-communication/shell-client.service";
import {HgPriceboxSectionComponent} from "./pricebox-section/hg-pricebox-section.component";
import {HedgeStateTransaction} from "./data-model/hedge-state-transaction";
import {LastQuoteCacheService} from "../last-quote-cache.service";
import {QuoteDto} from "../shell-communication/dtos/quote-dto.class";
import {MessageBusService} from "../message-bus.service";
import {HgStrategiesSectionComponent} from "./strategies-section/hg-strategies-section.component";
import {HgZonesGridSectionComponent} from "./zones-grid-section/hg-zones-grid-section.component";
import {ToastrService} from "ngx-toastr";
import {PositionsStateService} from "../adjustment-pricing-grid/services/positions-before-state.service";
import {ApgDataService} from "../adjustment-pricing-grid/services/apg-data.service";
import {HgNormalizationService} from "./services/hg-normalization.service";
import {HgHedgeMatrixComponent} from "./hedge-matrix/hg-hedge-matrix.component";
import {AccessControlService} from "../access-control-service.class";
import {
    EventStreamConnectionChangedUIMessage
} from "../ui-messages/event-stream-connection-changed-message-ui-message.interface";
import {filter, take, takeUntil} from "rxjs/operators";
import {Subject, Subscription} from "rxjs";
import {environment} from "../environments/environment";
import {HgTestingComponent} from "./hg-testing.component";
import {ComboHighlightedUIMessage} from "../ui-messages/ui-messages";
import {HedgePositionsService} from "./positions-section/hedge-positions/hedge-positions.service";
import {ApplyResult, CoPilotBookHedge, IHedgingGridCopilotFacade} from "./copilot/copilot-facade";
import {Leg, PackageCard, SolutionId} from "./copilot/copilot-domain";
import {CoPilotPositionDto} from "./copilot/copilot-operations";
import {TradingInstrument} from "../trading-instruments/trading-instrument.class";
import {TradingInstrumentDisplayNameService} from "../trading-instruments/trading-instrument-display-name.service";
import {OptionExpirationDescriptor} from "../shell-communication/shell-dto-protocol";
import {HgCopilotComponent} from "./copilot/hg-copilot.component";
import {DxPopupComponent} from "devextreme-angular/ui/popup";
import {OrionWindowController} from "../copilot-shared/orion-window.controller";
import {OptionsChainService} from "../option-chains.service";
import {AdjustmentAvailabilityState} from './copilot/adjustment-availability-state';
import {TradingInstrumentsService} from "../trading-instruments/trading-instruments-service.interface";
import {TradingInstrumentKind} from "../trading-instruments/trading-instrument-kind.enum";
import {OpgEsContractCandidate} from "../copilot-shared/opg-copilot-facade";
import {EsFairValueService} from "./services/es-fair-value.service";
import {
    formatDayKey,
    HG_SELECT_PORTFOLIO_REQUEST_TOPIC,
    OpenSubjectRequest,
    SELECT_REQUEST_TTL_MS
} from "../action-board/action-board.model";

@Component({
    selector: 'ets-hedging-grid',
    templateUrl: 'hedging-grid.component.html',
    styleUrls: [
        './hedging-grid.component.scss',
        './hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class HedgingGridComponent implements OnInit, AfterViewInit, OnDestroy, IHedgingGridCopilotFacade {
    readonly adjustmentAvailability = new AdjustmentAvailabilityState(this._shellService, this._messageBus);
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellService: ShellClientService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
        private readonly _apgDataService: ApgDataService,
        private readonly _apgPositionsStateService: PositionsStateService,
        private readonly _accessControlService: AccessControlService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _normalizationService: HgNormalizationService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _optionChains: OptionsChainService,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _esFairValue: EsFairValueService
    ) {
    }

    // ===== IHedgingGridCopilotFacade (CR-20260619-001) ====================================
    // CoPilot is the HedgeMatrix CoPilot, hosted on the composition root. ALL reads + the CLOSE Preview
    // WRITE source from the Hedge MATRIX (Increment 1c) — the Hedge Pad (settings-section) is no longer in
    // the CoPilot path. isEntitled reuses the host's own isCoPilotAvailable() secure-element gate.
    private _copilotCloseHedgeBySolution: { [solutionId: string]: string } = {};

    isEntitled(): boolean {
        return this.isCoPilotAvailable();   // the host's own CoPilot secure-element gate (no HedgePad)
    }

    readWorkingStatePositions(): CoPilotPositionDto[] {
        return this.matrixSectionCmp ? this.matrixSectionCmp.copilotReadWorkingStatePositions() : [];
    }

    readBookDates(): Date[] {
        return this.matrixSectionCmp ? this.matrixSectionCmp.copilotReadBookDates() : [];
    }

    readBookHedges(): CoPilotBookHedge[] {
        return this.matrixSectionCmp ? this.matrixSectionCmp.copilotReadBookHedges() : [];
    }

    readAdjustDates(): Date[] {
        return this.matrixSectionCmp ? this.matrixSectionCmp.copilotReadAdjustDates() : [];
    }

    isCopilotHedgeModified(groupId: string): boolean {
        return this.matrixSectionCmp ? this.matrixSectionCmp.isCopilotHedgeModified(groupId) : false;
    }

    isCopilotHedgeGone(tickers: string[]): boolean {
        return this.matrixSectionCmp ? this.matrixSectionCmp.isCopilotHedgeGone(tickers) : false;
    }

    readTargetInstrument(): TradingInstrument | null {
        return this.matrixSectionCmp ? (this.matrixSectionCmp.tradingInstrument || null) : null;
    }
    readPresetPortfolio(): ApgPortfolio | null { return this.selectedPortfolio || null; }

    // Per-leg ES->SPX normFactor for the Solution-card net price — byte-mirror of the sections' closure
    // (hg-pricebox-section.component.ts:60). Base = the loaded portfolio's asset (the SAME base the
    // pricebox/strategies sections use), so the card normalizes ES toward SPX-equivalent identically.
    // 1 for SPX/base or a void asset; 0.5 for ES futures. DISTINCT from readDefaultQty (quantity normalizer).
    getNormFactor(asset: string): number {
        const base = this.selectedPortfolio ? this.selectedPortfolio.asset : null;
        if (!asset || !base || asset === base) {
            return 1;
        }
        return this._normalizationService.getPointValueRatio(asset, base);
    }

    // The loaded portfolio's default order quantity — the HCF/defaultQty denominator for the Solution-card
    // net price (getGroupTotal's defaultQty arg). undefined until resolved on portfolio selection.
    readDefaultQty(): number | undefined {
        return this.portfolioDefaultQty;
    }

    // The loaded portfolio's own asset — the SAME base getNormFactor above resolves against, read from
    // the one place, so the browser's live card price and the backend's delivered price can never
    // normalize an ES leg toward two different bases (CR-20260910-001 milestone 2, ruling L15).
    readBaseAsset(): string | null {
        return this.selectedPortfolio ? (this.selectedPortfolio.asset || null) : null;
    }

    resolveNearestStrikeExpiration(): Promise<OptionExpirationDescriptor | null> {
        if (!this.matrixSectionCmp || !this.matrixSectionCmp.hedgeMatrixDataService) {
            return Promise.resolve(null);
        }
        return Promise.resolve(this.matrixSectionCmp.hedgeMatrixDataService.getNearestExpiration() || null);
    }

    readAvailableExpirations(): string[] {
        if (!this.matrixSectionCmp || !this.matrixSectionCmp.hedgeMatrixDataService) {
            return [];
        }
        return this.matrixSectionCmp.hedgeMatrixDataService.getAvailableExpirationDates() || [];
    }

    // CR-20260804-001: the atmOffset 'chosen' anchor — the Matrix's own center strike (the
    // user-facing middle of the ladder, the panel's equivalent of OPG's chosen ATM).
    readChosenAtm(): number | null {
        if (!this.matrixSectionCmp) {
            return null;
        }
        const center = this.matrixSectionCmp.centerStrike;
        return typeof center === 'number' && isFinite(center) ? center : null;
    }

    // CR-20260804-001: the 'market' anchor — the live market ATM for the matrix underlying.
    readDynamicAtm(): number | null {
        if (!this.matrixSectionCmp) {
            return null;
        }
        const atm = this.matrixSectionCmp.getAtmStrike();
        return typeof atm === 'number' && isFinite(atm) ? atm : null;
    }

    // CR-20260804-001 (the two-copilots reversal): the VISIBLE-filtered chain descriptors —
    // the embedded Select Copilot's expiration universe (date pills, strike pickers, leg
    // tickers). Empty until the matrix resolves its chain.
    readAvailableExpirationDescriptors(): OptionExpirationDescriptor[] {
        if (!this.matrixSectionCmp || !this.matrixSectionCmp.hedgeMatrixDataService) {
            return [];
        }
        return this.matrixSectionCmp.hedgeMatrixDataService.getAvailableExpirations() || [];
    }

    // ===== ORION ES mode (CR-20260924-002) ====================================================
    // The ORION window's own ES toggle: SESSION-ONLY component state (never a user setting) and
    // INDEPENDENT of the Hedge Matrix's ES hedging flag (owner ruling 2026-09-24). The trader keeps
    // typing SPX; the backend lens returns every answer on the ES contract; previews land in the
    // ES silo. Off at panel start; the copilot child resets it on a conversation boundary.
    orionEsMode = false;

    // ES chains fetched for the date block, per contract, for a candidate WITHOUT a silo (a silo
    // contract's chain lives in the matrix data service and is read from there).
    private _esChainByContract: { [contract: string]: OptionExpirationDescriptor[] } = {};

    // The fair-value gap for a candidate WITHOUT a silo, read at toggle-on through the SAME service
    // the matrix builds its silos on (EsFairValueService: backend fair value, else |ES − SPX| off the
    // browser's quote cache, rounded to the strike step, cached per day). Sent with the candidate so
    // the backend lens translates on exactly the number the silo will later be created on. Owner
    // smoke round 1 (2026-09-24): the backend's own read came back empty — the scraped source is
    // gone and PriceDataService's fallback has no live ES snapshot — so the browser's number leads.
    private _esGapByContract: { [contract: string]: number } = {};

    get isOrionEsEligible(): boolean {
        return !!this.matrixSectionCmp && this.matrixSectionCmp.isEsEligible;
    }

    // The title-bar switch. `$event.event` is DevExtreme's user-vs-programmatic discriminator: the
    // copilot's own reset (setOrionEsMode(false)) re-renders the switch and must not re-enter here.
    @DetectMethodChanges()
    onOrionEsModeChanged($event: any): void {
        if (!$event || !$event.event) {
            return;
        }
        this.setOrionEsMode(!!$event.value);
    }

    // The custom title template has no built-in close button; a close is a MINIMIZE (onCopilotPopupHidden).
    @DetectMethodChanges()
    closeCopilotPopup(): void {
        this.copilotPopupVisible = false;
    }

    readOrionEsMode(): boolean {
        return this.orionEsMode;
    }

    setOrionEsMode(on: boolean): void {
        if (this.orionEsMode === on) {
            return;
        }
        this.orionEsMode = on;
        if (on) {
            this.loadEsContext();
        }
        this._messageBus.publish({ topic: 'Hg.OrionEsModeChanged', payload: { enabled: on } });
        this._changeDetector.detectChanges();
    }

    readMatrixEsHedgingEnabled(): boolean {
        return !!this.matrixSectionCmp && !!this.matrixSectionCmp.esHedgingEnabled;
    }

    // The contracts the backend lens may translate onto, NEAREST FIRST: the existing ES silos (with
    // the gap their rows were built on, so a card strike and its silo row can never disagree), else
    // the nearest unexpired ES future — the underlying picker's own rule. Null when nothing qualifies.
    readEsContractCandidates(): OpgEsContractCandidate[] | null {
        const ds = this.matrixSectionCmp ? this.matrixSectionCmp.hedgeMatrixDataService : null;
        const out: OpgEsContractCandidate[] = [];
        if (ds) {
            ds.getActiveNonBaseUnderlyings().forEach(c => out.push({ contract: c, gap: ds.getFairValueOffset(c) }));
        }
        if (out.length === 0) {
            this.nearestEsContracts(1).forEach(t => out.push({
                contract: t,
                gap: this._esGapByContract.hasOwnProperty(t) ? this._esGapByContract[t] : null
            }));
        }
        out.sort((a, b) => this.esContractExpiryMs(a.contract) - this.esContractExpiryMs(b.contract));
        return out.length > 0 ? out : null;
    }

    // The VISIBLE expirations of one ES contract's chain: the silo's own when the silo exists, else
    // the chain fetched on toggle-on; empty until it arrives.
    readEsExpirationDescriptors(contract: string): OptionExpirationDescriptor[] {
        const ds = this.matrixSectionCmp ? this.matrixSectionCmp.hedgeMatrixDataService : null;
        if (ds && ds.getActiveNonBaseUnderlyings().indexOf(contract) >= 0) {
            return ds.getAvailableExpirations(contract) || [];
        }
        return this._esChainByContract[contract] || [];
    }

    // Toggle-on: for every candidate without a silo, fetch its ES chain (the date block) and its
    // fair-value gap (the lens); each arrival republishes the mode signal so the window re-reads.
    private loadEsContext(): void {
        (this.readEsContractCandidates() || []).forEach(c => {
            if (this.readEsExpirationDescriptors(c.contract).length === 0) {
                this._optionChains.getChain(c.contract).then(resp => {
                    this._esChainByContract[c.contract] = ((resp && resp.expirations) || []).filter(x => x.visible);
                    this.publishOrionEsModeChanged();
                }).catch(e => console.warn('[ORION ES mode] chain unavailable for ' + c.contract, e));
            }
            if (c.gap === null && !this._esGapByContract.hasOwnProperty(c.contract)) {
                this._esFairValue.getFairValue(c.contract).then(gap => {
                    // The service answers 0 when it could find neither a fair value nor live prices —
                    // not a gap; leave it unknown so the backend tries its own reads.
                    if (typeof gap === 'number' && isFinite(gap) && gap !== 0) {
                        this._esGapByContract[c.contract] = gap;
                        this.publishOrionEsModeChanged();
                    }
                }).catch(e => console.warn('[ORION ES mode] fair value unavailable for ' + c.contract, e));
            }
        });
    }

    private publishOrionEsModeChanged(): void {
        this._messageBus.publish({ topic: 'Hg.OrionEsModeChanged', payload: { enabled: this.orionEsMode } });
        this._changeDetector.detectChanges();
    }

    private nearestEsContracts(count: number): string[] {
        return this._tiService.getAllTradingInstruments()
            .filter(x => x.kind === TradingInstrumentKind.Futures && x.ticker.startsWith('ES') && !isInstrumentExpired(x))
            .sort((a, b) => this.esContractExpiryMs(a.ticker) - this.esContractExpiryMs(b.ticker))
            .slice(0, count)
            .map(x => x.ticker);
    }

    private esContractExpiryMs(ticker: string): number {
        const ti = this._tiService.getInstrumentByTicker(ticker);
        return ti && ti.expirationDate ? new Date(ti.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
    }

    resetCopilotDivergence(): void {
        if (this.settingsSectionCmp) {
            this.settingsSectionCmp.resetCopilotDivergence();
        }
        if (this.matrixSectionCmp) {
            this.matrixSectionCmp.resetCopilotMatrixDivergence();   // re-arm the Matrix close-Preview latch
        }
    }

    // CLOSE Preview -> Hedge Matrix. A close Solution is ONE hedge (all its legs share groupId).
    // Everything else — new hedges, and since CR-20260724-001 stage 7 also CHANGES to existing ones —
    // goes to the Matrix package path below.
    async applySolution(solutionId: SolutionId, legs: Leg[], cards?: PackageCard[], prePlacementGuard?: () => boolean): Promise<ApplyResult> {
        // An attributed package can be close-only too (a fold that closes a hedge outright), but it
        // names its targets on the CARDS and may change several hedges at once, so it must not take
        // the Close tab's one-hedge shortcut. The presence of a targeted card is what separates them.
        const hasTargetCards = !isVoid(cards) && cards.some(c => !isVoid(c.targetHedgeId));
        const isCloseOnly = !hasTargetCards
            && !isVoid(legs) && legs.length > 0 && legs.every(l => l.effect === 'close');
        if (isCloseOnly) {
            if (isVoid(this.matrixSectionCmp)) {
                return { applied: false, reason: 'missing-target-leg' };
            }
            const hedgeId = legs[0].groupId;
            if (!hedgeId) {
                return { applied: false, reason: 'missing-target-leg' };
            }
            const result = this.matrixSectionCmp.previewCopilotClose(hedgeId);
            if (result.applied) {
                this._copilotCloseHedgeBySolution[solutionId] = hedgeId;
            }
            return result;
        }
        // Everything else is a package, and it goes to the MATRIX (CR-20260724-001). Its cards decide
        // what each one lands as: a new hedge, or a change to one the book already holds (stage 7).
        //
        // They used to go to the Hedge Pad, which is why an Adjust Preview appeared to do nothing:
        // the Pad and the Matrix hold INDEPENDENT in-memory state, so the legs were placed on a
        // surface the trader was not looking at. That routing was left over from before Close was
        // re-homed here — Select and Adjust were the only users of it, and neither had shipped.
        if (isVoid(this.matrixSectionCmp)) {
            // Named for the same reason every exit inside previewCopilotOpen now is: one toast
            // covers many causes, and guessing which one fired costs a diagnosis round.
            console.warn('[CoPilot Preview REFUSED] the Hedge Matrix section is not present');
            return { applied: false, reason: 'unplaceable-leg' };
        }
        return this.matrixSectionCmp.previewCopilotOpen(solutionId, legs, cards, prePlacementGuard);
    }

    hasAdjustmentPreview(solutionId: string): boolean {
        return this.matrixSectionCmp?.hasAdjustmentPreview(solutionId) === true;
    }

    forgetAdjustmentPreview(solutionId: string): void {
        this.matrixSectionCmp?.forgetAdjustmentPreview(solutionId);
    }

    bindAvailableAdjustment(solutionId: SolutionId,
        reference: import('./copilot/available-adjustment-operations').AdjustmentAcquisitionReference): void {
        this.matrixSectionCmp?.bindAvailableAdjustment(solutionId, reference);
    }

    async removeCopilotLegs(solutionId: SolutionId): Promise<void> {
        const hedgeId = this._copilotCloseHedgeBySolution[solutionId];
        if (!isVoid(hedgeId)) {
            // Revert FIRST, then forget — so a failed revert never orphans the tracking entry (M1).
            if (this.matrixSectionCmp) {
                this.matrixSectionCmp.unPreviewCopilotClose(hedgeId);
            }
            delete this._copilotCloseHedgeBySolution[solutionId];
            return;
        }
        if (this.matrixSectionCmp) {
            // New-leg previews: the Matrix owns the tracking, so an unknown solutionId is a no-op.
            this.matrixSectionCmp.unPreviewCopilotOpen(solutionId);
        }
    }
    // ===== end IHedgingGridCopilotFacade ==================================================

    private _isZonesGridLinked: boolean;

    private _loadingClientsCount = 0;

    private _quoteIx: { [ix: string]: { multiplier: number, data: { price: number } }[] } = {};

    isLoading: boolean;

    positionsSectionCollapsed: boolean;

    settingsSectionCollapsed: boolean;

    matrixSectionCollapsed: boolean;

    strategiesSectionCollapsed: boolean;

    priceboxSectionCollapsed: boolean;

    zonesGridSectionCollapsed: boolean;

    // CoPilot floating window state (CR-20260619-001). The launcher button + window replace the old
    // collapsible CoPilot section. The window stays DRAGGABLE + RESIZABLE, but is confined to the
    // browser viewport (CR HicgA7n5 superseded "if he drags it, that's his problem").
    copilotPopupVisible = false;

    // CoPilot FAB position (A7/WI-11). null = the CSS default (matrix bottom-right, unchanged). A drag writes
    // {left, top} px here via (etsDraggablePositionChange); it lives on the component so it survives the FAB's
    // *ngIf teardown when the popup opens. Session only — a reload returns to the default corner.
    copilotFabPosition: { left: number; top: number } | null = null;

    // Preview ownership remains separate from the launcher's live suggestion count.
    copilotHasActivePreview = false;

    // CR: true while the CoPilot child reports the live Hedge Matrix has DRIFTED from its last sync (the same
    // condition that turns the in-popup refresh button yellow). Drives the COLLAPSED launcher's yellow/black
    // "drifted" background so the trader sees the drift even with the CoPilot window closed (when the in-popup
    // indicator is invisible). Surfaced from the child via (staleChange); the stale/sync state is owned there.
    copilotIsStale = false;

    // Dynamic height (owner 2026-06-23): the top edge is pinned COPILOT_TOP_OFFSET_PX below the WINDOW top;
    // the height auto-fits the child's content and grows DOWNWARD only, capped so the bottom gap equals the
    // top gap (symmetric margins = "most widened"), beyond which the child's dx-scroll-view scrolls. The cap
    // is WINDOW-relative, independent of any drag/resize. Tune the two px constants on visual smoke.
    private static readonly COPILOT_TOP_OFFSET_PX = 24;     // top edge offset == symmetric max bottom gap
    private static readonly COPILOT_CHROME_PX = 56;         // title bar + popup padding (empirically tuned: +20 so the bottom is not clipped)
    private static readonly COPILOT_MIN_HEIGHT_PX = 240;
    copilotPopupPosition: any = {
        my: 'center top', at: 'center top', of: window,
        offset: {x: 0, y: HedgingGridComponent.COPILOT_TOP_OFFSET_PX}
    };
    copilotPopupHeight = 420;                                // seed; corrected to content on the first measure
    private _copilotContentHeight = 0;

    // CR-20260804-001 (the two-copilots reversal): the Select tab's window widths. The HM
    // window chrome eats slightly more horizontal room than the OPG popup, so the OPG-tuned
    // 490/960 wrapped the "Follow-Through" / "Expected Range" pills here — the owner ruled
    // +20 px on BOTH Select modes at the 2026-08-04 smoke: single side 510, Calls & Puts
    // 980. 'none' = not on the Select tab — the HM copilot's own 490, unchanged.
    copilotPopupWidth = 490;

    @DetectMethodChanges()
    onCopilotWidthMode(mode: 'none' | 'normal' | 'wide'): void {
        this.copilotPopupWidth = mode === 'wide' ? 980 : (mode === 'normal' ? 510 : 490);
    }

    // The popup opened — let the child re-sync the embedded Select Copilot (the OPG host
    // calls onOpened() on every open; conversation continuity is decided inside).
    onCopilotPopupShown(): void {
        if (this.copilotCmp) {
            this.copilotCmp.onHostPopupShown();
        }
        // Re-assert the window zoom: the overlay element can be recreated when the popup's *ngIf
        // re-mounts it, and the edge strip must be (re)parented onto the overlay (CR-20260830-001).
        this.applyCopilotZoom();
    }

    // ---- ORION window chrome (zoom CR-20260830-001 + containment CR HicgA7n5) ------------------
    // The mechanism (scale-transform zoom, ear drag, MutationObserver re-assert, viewport clamp)
    // lives in the shared OrionWindowController — extracted 2026-08-31 so all three ORION hosts
    // (HG / OPG / APG) share it (owner: "this zoom hatch needs to be implemented for every
    // copilot"). These thin wrappers keep the template bindings + @DetectMethodChanges (OnPush)
    // on the host. Containment (owner smoke 2026-08-31): the window stays FULLY inside the
    // viewport — dx-popup's own containment (dragOutsideBoundary removed; this was the ONLY popup
    // in the codebase that set it) covers the title-bar drag, the controller's clamp covers the
    // ear drag + any programmatic transform write. Supersedes "if he drags it, that's his problem".
    private readonly _orionWindow = new OrionWindowController(() => this.copilotPopupRef);

    @ViewChild('copilotPopupRef', {static: false})
    copilotPopupRef: DxPopupComponent;

    get copilotZoomLevel(): number {
        return this._orionWindow.zoomLevel;
    }

    get copilotZoomPct(): number {
        return this._orionWindow.zoomPct;
    }

    @DetectMethodChanges()
    copilotZoomIn(): void {
        this._orionWindow.zoomIn();
    }

    @DetectMethodChanges()
    copilotZoomOut(): void {
        this._orionWindow.zoomOut();
    }

    @DetectMethodChanges()
    onCopilotZoomStripDblClick(ev: MouseEvent): void {
        this._orionWindow.onStripDblClick(ev);
    }

    onCopilotZoomStripPointerDown(ev: PointerEvent): void {
        this._orionWindow.onStripPointerDown(ev);
    }

    private applyCopilotZoom(): void {
        this._orionWindow.apply();
    }

    // --------------------------------------------------------------------------------------------

    @ViewChild('matrixColumn', {static: false})
    private _matrixColumnRef: ElementRef;

    @ViewChild(HgPositionsSectionComponent, {static: false})
    positionsSectionCmp: HgPositionsSectionComponent;

    @ViewChild(HgSettingsSectionComponent, {static: false})
    settingsSectionCmp: HgSettingsSectionComponent;

    // The CoPilot child (CR-20260804-001) — reached only to relay the popup-shown signal to
    // the embedded Select Copilot; everything else flows through the ctx facade as before.
    @ViewChild(HgCopilotComponent, {static: false})
    copilotCmp: HgCopilotComponent;

    @ViewChild(HgHedgeMatrixComponent, {static: false})
    matrixSectionCmp: HgHedgeMatrixComponent;

    @ViewChild(HgStrategiesSectionComponent, {static: false})
    strategiesSectionCmp: HgStrategiesSectionComponent;

    @ViewChild(HgPriceboxSectionComponent, {static: false})
    priceboxSectionCmp: HgPriceboxSectionComponent;

    @ViewChild(HgZonesGridSectionComponent, {static: false})
    zonesGridSectionCmp: HgZonesGridSectionComponent;

    showHedgePadIconsInHeader = true;

    portfolioDefaultQty?: number

    selectedPortfolio: ApgPortfolio;

    // Display name of the selected portfolio's BASE underlying (every portfolio's fixed
    // asset since CR-20260928-001; independent books included). Single source
    // bound to every shared section header's [underlying]. Null => no badge.
    underlyingDisplayName: string;

    ngOnInit() {
        this.adjustmentAvailability.changed.pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this._changeDetector.detectChanges());
        this.adjustmentAvailability.start();
        this.matrixSectionCollapsed = true;
        this.strategiesSectionCollapsed = true;
        this.priceboxSectionCollapsed = true;
        this.zonesGridSectionCollapsed = false;

        this._messageBus
            .of<QuoteDto[]>('QuoteDto')
            .subscribe(x => this.onQuotes(x.payload));

        this._messageBus
            .of<{ isLoading: boolean }>('Hg.Loading')
            .subscribe(x => {
                const isLoading = x.payload.isLoading;
                if (isLoading) {
                    this._loadingClientsCount++;
                } else {
                    this._loadingClientsCount--;
                }
                this.isLoading = this._loadingClientsCount > 0;
                this._changeDetector.detectChanges();
            });

        // CR-20260922-001 — an Action Board card asked for this book (live path). CR-20260924-001
        // F4b: only the panel the host stamped takes it; the subscription dies with the panel.
        this._messageBus
            .of<OpenSubjectRequest>(HG_SELECT_PORTFOLIO_REQUEST_TOPIC)
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => {
                if (this.takesSelectRequest(x.payload)) {
                    this.applySelectPortfolioRequest(x.payload, 0);
                }
            });
    }

    ngOnDestroy(): void {
        this.adjustmentAvailability?.dispose();
        ++this._opportunityRequest;
        this._openOpportunityOnActivation = null;
        this._pendingExpiry?.unsubscribe();
        this._pendingOpportunity?.unsubscribe();
        clearTimeout(this._pendingOpportunityDeadline);
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    private readonly _unsubscriber = new Subject<void>();
    private _pendingExpiry: Subscription | null = null;
    private _pendingOpportunity: Subscription | null = null;
    private _pendingOpportunityDeadline: any;
    private _opportunityRequest = 0;
    availableAdjustmentTarget: string | null = null;
    availableAdjustmentNavigation = 0;
    private _panelActive = true;
    private _openOpportunityOnActivation: (() => void) | null = null;

    @Input()
    set panelActive(value: boolean) {
        this._panelActive = value;
        if (value) {
            const open = this._openOpportunityOnActivation;
            this._openOpportunityOnActivation = null;
            open?.();
        }
    }

    /** CR-20260924-001 F4b: the workspace panel id this grid lives in (from the panel wrapper). */
    @Input()
    panelId: string;

    @Input() layoutTabId: string;

    private takesSelectRequest(request: OpenSubjectRequest): boolean {
        return !!request
            && (!request.sourceLayoutTabId || request.sourceLayoutTabId === this.layoutTabId)
            && (!request.targetPanelId || request.targetPanelId === this.panelId);
    }

    @DetectMethodChanges()
    ngAfterViewInit() {
        this.checkIfSettingsSectionIsShortVersion();

        // CR-20260922-001 — boot path of the deep link: the selector fills its portfolios
        // asynchronously, so retry a few times before giving up on a fresh cached request.
        const pending = this._messageBus.getLastMessage<OpenSubjectRequest>(HG_SELECT_PORTFOLIO_REQUEST_TOPIC);
        if (pending && pending.payload && Date.now() - pending.payload.at < SELECT_REQUEST_TTL_MS
            && this.takesSelectRequest(pending.payload)) {
            this._messageBus.clearLastMessage(HG_SELECT_PORTFOLIO_REQUEST_TOPIC);
            setTimeout(() => this.applySelectPortfolioRequest(pending.payload, 8), 500);
        }
    }

    // CR-20260922-001. Selects the requested book through the selector (so every section follows,
    // exactly as a click would) and then the requested expiry in the Hedge Pad. CR-20260924-001 F5
    // (owner 2026-09-24): the grid opens with a PORTFOLIO; the expiry is picked only once that
    // portfolio has loaded completely — never on a timer against whatever list is on screen.
    private applySelectPortfolioRequest(request: OpenSubjectRequest, retriesLeft: number): void {
        if (!request) {
            return;
        }

        const selector = this.positionsSectionCmp?.portfolioSelectorCmp;
        const all = selector?.portfolios?.flatMap(x => x.items) || [];

        if (isVoid(selector) || all.length === 0) {
            if (retriesLeft > 0) {
                setTimeout(() => this.applySelectPortfolioRequest(request, retriesLeft - 1), 500);
            } else {
                this._toastr.warning('The Hedging Grid has no portfolios loaded to open the Action Board item on');
            }
            return;
        }

        const portfolio = all.find(x => x.id === request.portfolioId && x.userId === request.userId);
        if (isVoid(portfolio)) {
            this._toastr.warning('The portfolio the Action Board asked for is not loaded in this panel');
            return;
        }

        const alreadySelected = this.selectedPortfolio
            && this.selectedPortfolio.id === portfolio.id
            && this.selectedPortfolio.userId === portfolio.userId;

        const revision = ++this._opportunityRequest;
        this._openOpportunityOnActivation = null;
        this._pendingOpportunity?.unsubscribe();
        clearTimeout(this._pendingOpportunityDeadline);
        this.availableAdjustmentTarget = null;
        if (request.itemType === 'AvailableAdjustment') {
            const open = () => {
                clearTimeout(this._pendingOpportunityDeadline);
                // The host sends the request before activating the workspace. An already-loaded
                // book can otherwise open its popup under the still-hidden panel, and lose it.
                if (!this._panelActive) { this._openOpportunityOnActivation = open; return; }
                // PortfolioSelected is emitted while the loading/widget callback is still active.
                // Open on the next UI turn, after loading and workspace activation have settled.
                setTimeout(() => {
                    if (revision !== this._opportunityRequest || this.selectedPortfolio?.id !== portfolio.id
                        || this.selectedPortfolio?.userId !== portfolio.userId || !this.isCoPilotAvailable()
                        || Date.now() - request.at >= SELECT_REQUEST_TTL_MS) { return; }
                    if (!this._panelActive) { this._openOpportunityOnActivation = open; return; }
                    this.onExpandMatrixRequest();
                    this.availableAdjustmentTarget = request.actionItemId;
                    this.availableAdjustmentNavigation++;
                    this.openCopilot();
                    this._changeDetector.detectChanges();
                    // Reuse the existing popup API: a visible binding applied while its workspace was
                    // hidden can leave DevExtreme's overlay hidden even after the book has loaded.
                    this.copilotPopupRef?.instance?.show();
                }, 0);
            };
            if (alreadySelected && !this.isLoading) { open(); }
            else {
                // Subscribe before selecting: a cached book may finish loading synchronously.
                this._pendingOpportunity = this._messageBus.of<{portfolio: ApgPortfolio}>('Hg.PortfolioSelected')
                    .pipe(filter(x => x.payload?.portfolio?.id === portfolio.id
                        && x.payload?.portfolio?.userId === portfolio.userId), take(1), takeUntil(this._unsubscriber))
                    .subscribe(open);
                this._pendingOpportunityDeadline = setTimeout(() => this._pendingOpportunity?.unsubscribe(), SELECT_REQUEST_TTL_MS);
            }
        }

        if (!alreadySelected) {
            selector.onPortfolioChange(portfolio);
        }

        if (request.expirationDayKey) {
            if (alreadySelected && !this.isLoading) {
                this.selectExpiration(request.expirationDayKey);
            } else {
                this.selectExpirationWhenLoaded(portfolio, request.expirationDayKey);
            }
        }
    }

    /**
     * F5: wait for the book's load to complete — 'Hg.PortfolioSelected' is published after every
     * section (the Hedge Pad's chain included) has finished loading it — then pick the expiry once.
     * A selection the selector refused (book held by another panel) never completes; the wait is
     * dropped after a minute. A newer request replaces an older wait.
     */
    private selectExpirationWhenLoaded(portfolio: ApgPortfolio, dayKey: string): void {
        this._pendingExpiry?.unsubscribe();

        const guard = setTimeout(() => this._pendingExpiry?.unsubscribe(), 60000);
        this._pendingExpiry = this._messageBus
            .of<{ portfolio: ApgPortfolio }>('Hg.PortfolioSelected')
            .pipe(
                filter(x => x.payload?.portfolio?.id === portfolio.id && x.payload?.portfolio?.userId === portfolio.userId),
                take(1),
                takeUntil(this._unsubscriber)
            )
            .subscribe(() => {
                clearTimeout(guard);
                this.selectExpiration(dayKey);
            });
    }

    private selectExpiration(dayKey: string): void {
        const settings = this.settingsSectionCmp;
        const match = settings?.expirationList?.find(x => x.optionExpirationDate === dayKey);

        if (isVoid(settings) || isVoid(match)) {
            this._toastr.info(`Expiry ${formatDayKey(dayKey)} is not in the loaded chain; select it by hand`);
            return;
        }

        settings.selectedExpiration = match;
        this._changeDetector.detectChanges();
    }

    @DetectMethodChanges()
    toggleSectionCollapsed(section: 'positions' | 'settings' | 'matrix' | 'strategies' | 'pricebox' | 'zones-grd') {
        switch (section) {
            case "positions":
                this.positionsSectionCollapsed = !this.positionsSectionCollapsed;
                break;
            case "settings":
                this.settingsSectionCollapsed = !this.settingsSectionCollapsed;
                if (!this.settingsSectionCollapsed) {
                    if (!this.matrixSectionCollapsed) {
                        this.onExpandHedgePadRequest();
                    }
                }
                break;
            case "matrix":
                this.matrixSectionCollapsed = !this.matrixSectionCollapsed;
                if (!this.matrixSectionCollapsed) {
                    this.onExpandMatrixRequest();
                } else {
                    // CoPilot is a child of the matrix — collapsing the matrix closes the CoPilot window
                    // (the launcher is already hidden by *ngIf when the matrix is collapsed).
                    this.copilotPopupVisible = false;
                }
                this.matrixSectionCmp.onCollapsedStateChanged(this.matrixSectionCollapsed);
                break;
            case "strategies":
                this.strategiesSectionCollapsed = !this.strategiesSectionCollapsed;
                break;
            case "pricebox":
                this.priceboxSectionCollapsed = !this.priceboxSectionCollapsed;
                break;
            case "zones-grd":
                this.zonesGridSectionCollapsed = !this.zonesGridSectionCollapsed;
                break;
        }
        this.checkIfSettingsSectionIsShortVersion();
    }

    private checkIfSettingsSectionIsShortVersion() {
        const anyPricingOpen = !this.priceboxSectionCollapsed || !this.strategiesSectionCollapsed ||
            !this.zonesGridSectionCollapsed || !this.matrixSectionCollapsed;

        if (anyPricingOpen) {
            this.settingsSectionCmp.isShortVersion = true;
        } else {
            this.settingsSectionCmp.isShortVersion = false;
        }

        this.showHedgePadIconsInHeader = this.settingsSectionCmp.isShortVersion;
    }

    @DetectMethodChanges({isAsync: true})
    async onPortfolioSelected(args: ApgPortfolio) {

        this.isLoading = true;

        this._changeDetector.detectChanges();

        try {

            this.selectedPortfolio = args;
            this.adjustmentAvailability.setScope(args?.userId, args?.id, this.isCoPilotAvailable());

            try {
                // Base underlying of the portfolio (independent book => its fixed asset).
                // Use the portfolio resolver, NOT any leg/matrix underlying, so an
                // ES-hedged book still shows its SPX base. isVoid => hide the badge.
                const underlyingTicker = await this._apgDataService.getUnderlyingOfPortfolio(args);
                this.underlyingDisplayName = isVoid(underlyingTicker)
                    ? null
                    : this._displayNameService.getDisplayNameForTicker(underlyingTicker);
            } catch (e) {
                this.underlyingDisplayName = null;
            }

            this.portfolioDefaultQty = await this.getDefaultQtyForPortfolio(args);

            try {
                await this.settingsSectionCmp?.onPortfolioSelected(args);
            } catch (e) {}

            try {
                await this.matrixSectionCmp.onPortfolioSelected(args);
            } catch (e) {}

            try {
                this.strategiesSectionCmp?.onPortfolioSelected(args);
            } catch (e) {}

            try {
                await this.priceboxSectionCmp?.onPortfolioSelected(args);
            } catch(e) {}

            try {
                this.zonesGridSectionCmp?.onPortfolioSelected(args);
            } catch(e) {}
        } finally {
            this._messageBus.publish({
                topic: 'Hg.PortfolioSelected',
                payload: {
                    portfolio: args
                }
            });
            this.isLoading = false;
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onReviewChangesClicked(byAfterState?: boolean) {

        const currentPositions =
            (this.positionsSectionCmp.hedgePositionsCmp.hedgePositions || []).slice();

        const hedgePositionsValid = currentPositions.every(x => x.isValid());

        if (!hedgePositionsValid) {
            this._toastr.error('Fix Hedge Positions\' Configuration To Review Changes');
            return;
        }

        const desiredPositions = (this.settingsSectionCmp.positions || [])
            .filter(x => !x.removed)
            .sort((a, b) => b.strike - a.strike)
            .slice();

        const desiredPositionsValid = desiredPositions.every(x => x.isValid());

        if (!desiredPositionsValid) {
            this._toastr.error('Fix Desired Positions\' Configuration To Review Changes')
            return;
        }


        //
        // Calls
        //
        const currentCalls = currentPositions
            .filter(x => x.type === 'Call')
            .map(x => x.asDto());

        const currentCallGroups = Enumerable.from(currentCalls)
            .groupBy(x => x.groupId)
            .toArray()
            .map(x => {
                const grp: HedgePositionGroup = {
                    groupId: x.key(),
                    label: x.first().label,
                    color: x.first().color,
                    side: 'Calls',
                    positions: x.toArray()
                };

                return grp;
            });

        const desiredCalls = desiredPositions
            .filter(x => x.type === 'Call')
            .map(x => x.asDto());

        const desiredCallGroups = Enumerable.from(desiredCalls)
            .groupBy(x => x.groupId)
            .toArray()
            .map(x => {
                const grp: HedgePositionGroup = {
                    groupId: x.key(),
                    label: x.first().label,
                    color: x.first().color,
                    side: 'Calls',
                    positions: x.toArray()
                };

                return grp;
            })

        const c: HedgePositionGroupsBySide = {
            side: 'Calls',
            currentState: currentCallGroups,
            desiredState: desiredCallGroups
        };

        if (this.settingsSectionCmp.priceToOpen) {
            c.currentState = [];
        }

        //
        // Puts
        //
        const currentPuts = currentPositions
            .filter(x => x.type === 'Put')
            .map(x => x.asDto());

        const currentPutGroups = Enumerable.from(currentPuts)
            .groupBy(x => x.groupId)
            .toArray()
            .map(x => {
                const grp: HedgePositionGroup = {
                    groupId: x.key(),
                    label: x.first().label,
                    color: x.first().color,
                    side: 'Puts',
                    positions: x.toArray()
                };

                return grp;
            });

        const desiredPuts = desiredPositions
            .filter(x => x.type === 'Put')
            .map(x => x.asDto());

        const desiredPutGroups = Enumerable.from(desiredPuts)
            .groupBy(x => x.groupId)
            .toArray()
            .map(x => {
                const grp: HedgePositionGroup = {
                    groupId: x.key(),
                    label: x.first().label,
                    color: x.first().color,
                    side: 'Puts',
                    positions: x.toArray()
                };

                return grp;
            })

        const p: HedgePositionGroupsBySide = {
            side: 'Puts',
            currentState: currentPutGroups,
            desiredState: desiredPutGroups
        };

        if (this.settingsSectionCmp.priceToOpen) {
            p.currentState = [];
        }

        const sides = [];

        if (!isVoid(currentCallGroups) || !isVoid(desiredCallGroups)) {
            sides.push(c);
        }

        if (!isVoid(currentPutGroups) || !isVoid(desiredPutGroups)) {
            sides.push(p);
        }

        if (isVoid(sides)) {
            this._toastr
                .error('Current configuration make no sense. ' +
                    'Please provide either current state or/and desired state of the hedge');
            return;
        }


        const qry = new CalculateCashFlowHedges(
            getShortUUID(),
            false,
            null,
            sides
        );

        this.isLoading = true;

        this._changeDetector.detectChanges();

        try {

            const reply = await this._shellService
                .processQuery<CalculateCashFlowHedgesReply>(qry);

            this.subscribeQuotes(reply.hedgeStateTransactions);

            this.strategiesSectionCmp.setData(reply.hedgeStateTransactions);

            this.priceboxSectionCmp.setData(reply.hedgeStateTransactions);

            const state = JSON.stringify(desiredPositions.map(x => x.asDto()));
            this.priceboxSectionCmp.setReviewedState(state)

            const portfolioPositions = this.positionsSectionCmp.portfolioPositionsCmp.portfolioPositions;

            this.zonesGridSectionCmp.setData(reply.hedgeStateTransactions, portfolioPositions);

            if (this._isZonesGridLinked) {
                await this.onZonesGridLoadDataClicked(true);
            } else if (byAfterState) {
                this.zonesGridSectionCmp.lastReviewedState = this.priceboxSectionCmp.lastReviewedState;
            }

        } finally {

            this.isLoading = false;

        }

        this._messageBus.publishAsync({
            topic: 'Hg.AfterReviewChanges',
            payload: {
                portfolio: this.selectedPortfolio
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    async onAcquireRequested(messages: ComboHighlightedUIMessage[]) {
        if (this.isLoading || !this.selectedPortfolio || !messages?.length) { return; }
        const portfolio = this.selectedPortfolio;
        const savedModels = (this.settingsSectionCmp.positions || []).map(x =>
            Object.assign(Object.create(Object.getPrototypeOf(x)), x));
        let committed = false;
        const requireCurrent = () => {
            if (this.selectedPortfolio !== portfolio) { throw new Error('The Selected Portfolio Changed.'); }
        };
        console.log('[Acquire] onAcquireRequested() called with', messages.length, 'messages');
        this.isLoading = true;

        try {
            const references = messages.map(x => x.availableAdjustment).filter(x => !!x);
            // Step 1: Paste each combo message into settings section sequentially
            const positionsBefore = (this.settingsSectionCmp.positions || []).length;
            console.log('[Acquire] Settings section positions before paste:', positionsBefore);

            for (let i = 0; i < messages.length; i++) {
                const msg = messages[i];
                console.log('[Acquire] Pasting message', i + 1, '/', messages.length,
                    '| strategyName:', msg.strategyName,
                    '| items:', msg.items?.length,
                    '| isTransaction:', !!msg.hedgingGridTransaction,
                    '| groupId:', msg.hedgingGridTransaction?.groupId);

                const pasted = await this.settingsSectionCmp.pasteComboFromClipboard(msg);
                requireCurrent();
                if (!pasted) { throw new Error('The Acquisition Could Not Be Applied.'); }

                console.log('[Acquire] Paste complete for message', i + 1,
                    '| positions now:', (this.settingsSectionCmp.positions || []).length);
            }

            // Step 2: Read the resulting desired positions
            const desiredPositions = (this.settingsSectionCmp.positions || [])
                .filter(x => !x.removed)
                .map(x => x.asDto());

            const uniqueGroups = desiredPositions.map(function(p) { return p.groupId; })
                .filter(function(v, i, a) { return a.indexOf(v) === i; });
            const uniqueLabels = desiredPositions.map(function(p) { return p.label; })
                .filter(function(v, i, a) { return a.indexOf(v) === i; });
            console.log('[Acquire] Desired positions after paste:', desiredPositions.length,
                '| groups:', uniqueGroups,
                '| labels:', uniqueLabels);

            // Step 3: Save as current positions
            console.log('[Acquire] Saving positions to settings...');
            requireCurrent();
            await this._hedgePositionsService.saveHedgePositions(
                desiredPositions, portfolio
            );
            committed = true;
            // Completion is independent: a failed notification must never repeat or undo the save.
            await this.completeAcquiredCards(portfolio, references);
            requireCurrent();
            console.log('[Acquire] Positions saved');

            // Step 4: Clear any pending after-state
            this._hedgePositionsService.clearAfterState(this.selectedPortfolio);
            console.log('[Acquire] After-state cleared');

            // Step 5: Reload hedge positions panel from saved state
            console.log('[Acquire] Reloading hedge positions panel...');
            await this.positionsSectionCmp.hedgePositionsCmp
                .onPortfolioSelected(this.selectedPortfolio);
            console.log('[Acquire] Hedge positions panel reloaded');

            // Step 6: Publish refresh — triggers settings section to reload from saved state
            console.log('[Acquire] Publishing Hg.AfterStateApplied for Calls and Puts');
            this._messageBus.publish({
                topic: 'Hg.AfterStateApplied',
                payload: {side: 'Calls', portfolio: this.selectedPortfolio}
            });
            this._messageBus.publish({
                topic: 'Hg.AfterStateApplied',
                payload: {side: 'Puts', portfolio: this.selectedPortfolio}
            });

            // Step 7: Reset base state to eliminate yellow "pending changes" border
            this.settingsSectionCmp.afterReviewChanges();
            console.log('[Acquire] Base state reset via afterReviewChanges()');

            this._toastr.success('Changes acquired');
            console.log('[Acquire] Acquire completed successfully');

        } catch (e) {
            if (!committed && this.selectedPortfolio === portfolio) {
                this.settingsSectionCmp.restoreAcquirePositions(savedModels);
            }
            console.error('[Acquire] Acquire Changes failed', e);
            if (committed) { this._toastr.warning('Changes Were Saved. Reload the Portfolio to Refresh the View.'); }
            else { this._toastr.error('Failed to Acquire Changes. ' + (e?.message || '')); }
        } finally {
            this.isLoading = false;
        }
    }

    private async completeAcquiredCards(portfolio: ApgPortfolio,
        references: import('./copilot/available-adjustment-operations').AdjustmentAcquisitionReference[], retry = false): Promise<void> {
        if (!references.length) { return; }
        try {
            await this._hedgePositionsService.completeAcquiredAdjustments(portfolio, references);
            if (retry) { this._toastr.success('Action Board Card Marked Done.'); }
        } catch (error) {
            console.error('[Acquire] Holdings saved; card completion failed', error);
            this._toastr.warning('Hedges Saved. Card Completion Failed. Click to Retry Card Completion.',
                undefined, {timeOut: 0, extendedTimeOut: 0}).onTap
                .pipe(take(1), takeUntil(this._unsubscriber))
                .subscribe(() => { void this.completeAcquiredCards(portfolio, references, true); });
        }
    }

    private subscribeQuotes(hedgeStateTransactions: HedgeStateTransaction[]) {

        hedgeStateTransactions
            .forEach(hst => {

                hst.positionTransactions.forEach(x => {

                    if (!isVoid(x.afterState)) {

                        x.afterState.positions.forEach(apos => {

                            let container = this._quoteIx[apos.ticker];

                            if (isVoid(container)) {
                                container = [];
                                this._quoteIx[apos.ticker] = container;
                            }

                            const data = {
                                data: apos,
                                multiplier: x.noChanges ? Math.sign(apos.qty) : Math.sign(apos.qty) * -1,
                            } as any;

                            container.push(data);
                        });
                    }

                    if (!isVoid(x.beforeState)) {

                        x.beforeState.positions.forEach(bpos => {

                            let container = this._quoteIx[bpos.ticker];

                            if (isVoid(container)) {
                                container = [];
                                this._quoteIx[bpos.ticker] = container;
                            }

                            const data = {
                                data: bpos,
                                multiplier: Math.sign(bpos.qty),
                            } as any;

                            container.push(data);
                        });
                    }
                });


                hst.orderTransactions.forEach(x => {

                    if (!isVoid(x.orders)) {

                        const hcf =
                            findHCF(x.orders.map(x => Math.abs(x.qty)));

                        x.orders.forEach(order => {

                            let container = this._quoteIx[order.ticker];

                            if (isVoid(container)) {
                                container = [];
                                this._quoteIx[order.ticker] = container;
                            }

                            const data = {
                                data: order,
                                multiplier: Math.sign(order.qty) * -1,
                                hcf
                            } as any;

                            container.push(data);
                        });
                    }

                    if (!isVoid(x.balancedOrders)) {

                        x.balancedOrders.forEach(bo => {
                            const hcf =
                                findHCF(bo.map(x => Math.abs(x.qty)));

                            bo.forEach(order => {

                                let container = this._quoteIx[order.ticker];

                                if (isVoid(container)) {
                                    container = [];
                                    this._quoteIx[order.ticker] = container;
                                }

                                const data = {
                                    data: order,
                                    multiplier: Math.sign(order.qty) * -1,
                                    hcf
                                } as any;

                                container.push(data);
                            });
                        });
                    }

                });
            });

        const totalTickers = Object.keys(this._quoteIx);

        const cachedQuotes = this._lastQuoteCache.subscribeTickers(totalTickers);

        if (isVoid(cachedQuotes)) {
            return;
        }

        const quoteDtos = Object.values(cachedQuotes);

        this.onQuotes(quoteDtos);
    }

    private onQuotes(quoteDtos: QuoteDto[]) {
        quoteDtos.forEach(quote => {
            const container = this._quoteIx[quote.ticker];

            if (!isVoid(container)) {
                container.forEach(x => {
                    x.data.price = quote.mid * x.multiplier;
                });
            }

            this.settingsSectionCmp?.positions?.forEach(p => {
                const ticker = p.getTicker();
                if (isVoid(ticker)) {
                    return;
                }
                if (ticker !== quote.ticker) {
                    return;
                }
                p.price = quote.mid;
            });

        });

        this._messageBus.publishAsync({
            topic: 'Hg.RefreshQuotes',
            payload: {
                portfolio: this.selectedPortfolio
            }
        });
    }

    onZonesGridLinkStateChanged(state: boolean) {
        this._isZonesGridLinked = state;
    }

    onZonesGridClearDataRequest() {
        this.zonesGridSectionCmp.clearData();
    }

    @DetectMethodChanges({isAsync: true})
    async onZonesGridLoadDataClicked(silently?: boolean) {
        this.isLoading = true;
        try {

            await this.zonesGridSectionCmp.loadDataClicked(silently);

            this.zonesGridSectionCmp.lastReviewedState = this.priceboxSectionCmp.lastReviewedState;

            this._messageBus.publishAsync({
                topic: 'Hg.AfterReviewChanges',
                payload: {
                    portfolio: this.selectedPortfolio
                }
            });

        } catch (e) {
            if (!isVoid(e) && !isVoid(e.message)) {
                this._toastr.error(e.message);
            }
        } finally {
            this.isLoading = false;
        }
    }

    @DetectMethodChanges()
    onExpandHedgePadRequest() {
        this.settingsSectionCollapsed = false;

        this.strategiesSectionCollapsed = this.priceboxSectionCollapsed =
            this.zonesGridSectionCollapsed = this.matrixSectionCollapsed = true;

        // This is a SECOND path that collapses the Hedge Matrix (besides toggleSectionCollapsed('matrix')).
        // CoPilot is a child of the matrix — keep the invariant "matrix collapsed => CoPilot window closed"
        // so the floating window is never orphaned with its launcher hidden.
        this.copilotPopupVisible = false;

        this.checkIfSettingsSectionIsShortVersion();
    }

    @DetectMethodChanges()
    onExpandMatrixRequest() {
        this.matrixSectionCollapsed = false;

        this.strategiesSectionCollapsed = this.priceboxSectionCollapsed =
            this.zonesGridSectionCollapsed = this.settingsSectionCollapsed = true;
    }

    isZonesGridDifferent(): boolean {

        if (isVoid(this.priceboxSectionCmp)) {
            return false;
        }

        if (isVoid(this.zonesGridSectionCmp)) {
            return false;
        }

        const settingsState = this.priceboxSectionCmp.lastReviewedState;

        const zonesState = this.zonesGridSectionCmp.lastReviewedState;

        if (isVoid(zonesState)) {
            return false;
        }

        if (isVoid(settingsState)) {
            return !isVoid(zonesState);
        }

        return settingsState !== zonesState;
    }

    isZonesGridOutdated(): boolean {
        return this.zonesGridSectionCmp?.isAtmOutdated();
    }

    canLoadZonesGrid(): boolean {
        return !isVoid(this.priceboxSectionCmp?.lastReviewedState);
    }

    showZonesGridMenu() {
        this.zonesGridSectionCmp?.showMenu();
    }

    private async getDefaultQtyForPortfolio(args: ApgPortfolio) : Promise<number> {

        // Independent HG portfolios (CR-20260606-001) carry their own default qty;
        // no APG template exists, so the lut.strategy deref below would throw.
        if (args?.isIndependent) {
            return isVoid(args.defaultQty) ? 1 : args.defaultQty;
        }

        const underlyingOfPortfolio = await this._apgDataService.getUnderlyingOfPortfolio(args);

        const lut = await this._apgDataService.getLastUsedTemplateOfPortfolio(args);

        const serviceConfig = {
            orientation: undefined,
            userId: args.userId,
            userName: args.userName
        };

        this._apgPositionsStateService.configure(serviceConfig);

        const dp = this._apgPositionsStateService.getDefaultsProvider(
            args.id,
            underlyingOfPortfolio,
            lut.strategy === 'Calls & Puts' ? 'double' : 'single'
        );

        return dp?.first?.orderQty;
    }

    isHedgingMatrixAvailable() {
        return this._accessControlService.isSecureElementAvailable('9931888b-4320-49b8-a0ce-951c897f5f40');
    }

    isCoPilotAvailable() {
        return this._accessControlService.isSecureElementAvailable('3ffd6a93-0d5a-4688-80ce-1c4d3d730043');
    }

    // Open the CoPilot floating window from the launcher. Re-checks the gates (entitlement + matrix
    // available + matrix expanded) defensively, then anchors the window over the matrix column once (so a
    // later change-detection pass does not reset a drag) and shows it. The launcher hides via *ngIf while
    // the window is open.
    @DetectMethodChanges()
    openCopilot() {
        // A portfolio must be selected — CoPilot operates on the loaded portfolio's positions (it makes no
        // sense without one). The launcher is already hidden by *ngIf until a portfolio is selected; this is
        // the defensive backstop.
        if (!this.isCoPilotAvailable() || !this.isHedgingMatrixAvailable() || this.matrixSectionCollapsed || !this.selectedPortfolio) {
            return;
        }
        // Top pinned below the window top; the height then auto-fits the content (onCopilotContentHeight).
        // The WINDOW is the reference frame (not the matrix column) so the top/bottom margins stay symmetric.
        this.copilotPopupPosition = {
            my: 'center top', at: 'center top', of: window,
            offset: {x: 0, y: HedgingGridComponent.COPILOT_TOP_OFFSET_PX}
        };
        this._applyCopilotPopupHeight();   // clamp the seed to the current window before the first measure
        this.copilotPopupVisible = true;
    }

    // The window was closed (title-bar X / programmatic). The launcher reappears via the visible flag. The
    // CoPilot child stays mounted, so an in-flight run + previewed legs are preserved for the next open.
    @DetectMethodChanges()
    onCopilotPopupHidden() {
        this.copilotPopupVisible = false;
    }

    // The CoPilot child reports its natural content height; we clamp it into the popup. The top is pinned,
    // so the popup grows DOWNWARD until the bottom gap equals the top offset, then the child's dx-scroll-view
    // takes over. WINDOW-relative cap — drag/resize never change it (owner: "if he drags it, his problem").
    @DetectMethodChanges()
    onCopilotContentHeight(contentHeight: number) {
        this._copilotContentHeight = contentHeight;
        this._applyCopilotPopupHeight();
    }

    // CR: the CoPilot child reports whether ANY Solution is currently previewed (any tab). Mirror it onto
    // the launcher's green state. OnPush: the decorator runs change detection, so the FAB's class updates
    // even though the change originates from a child @Output while the window may be closed (the child
    // stays mounted, so the binding stays live).
    @DetectMethodChanges()
    onCopilotPreviewActiveChange(active: boolean) {
        this.copilotHasActivePreview = active;
    }

    // CR: the CoPilot child reports whether the live matrix has drifted from its last sync. Mirror it onto the
    // launcher's yellow/black "drifted" state. Same OnPush rationale as onCopilotPreviewActiveChange: the
    // child stays mounted while the window is closed, so the binding stays live and the FAB repaints even
    // though the trigger originates from a child @Output while collapsed.
    @DetectMethodChanges()
    onCopilotStaleChange(stale: boolean) {
        this.copilotIsStale = stale;
    }

    private _applyCopilotPopupHeight(): void {
        const maxH = this._copilotMaxHeight();
        const desired = this._copilotContentHeight + HedgingGridComponent.COPILOT_CHROME_PX;
        const min = HedgingGridComponent.COPILOT_MIN_HEIGHT_PX;
        this.copilotPopupHeight = Math.max(min, Math.min(desired, maxH));
    }

    // maxHeight = the full window minus an equal gap top + bottom (COPILOT_TOP_OFFSET_PX each). Floored at the
    // min so a very short window never collapses the popup (the symmetric gap is then sacrificed — accepted).
    private _copilotMaxHeight(): number {
        const symmetric = window.innerHeight - 2 * HedgingGridComponent.COPILOT_TOP_OFFSET_PX;
        return Math.max(HedgingGridComponent.COPILOT_MIN_HEIGHT_PX, symmetric);
    }

    // Window resize changes the cap; re-clamp the live popup so it never spills past the symmetric margins.
    @HostListener('window:resize')
    onWindowResize() {
        this._onWindowResizeImpl();
    }

    @DetectMethodChanges()
    private _onWindowResizeImpl(): void {
        if (this.copilotPopupVisible) {
            this._applyCopilotPopupHeight();
            // CR HicgA7n5: a resize (incl. browser-zoom changes) can shrink the viewport past the
            // window's position — re-clamp so the window stays fully inside it.
            this._orionWindow.clampNow();
        }
    }

    // -----------

    @ViewChild(HgTestingComponent) testingCmp: HgTestingComponent;

    protected showTestingPanel() {
        if (environment.production) {
            return;
        }
        this.testingCmp.show();
    }
}
